<script setup>
import { computed } from 'vue';
import { roomLabel } from '../../../helpers/roomVisits';
import XlButton from '../../xl/XlButton.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import { DELETE_REFUSED } from '../../../helpers/sessionCard';
import IconClose from '../../icons/IconClose.vue';
import IconVideo from '../../icons/IconVideo.vue';

/**
 * A session from Today, previewed — and a way in, which is the one thing
 * anybody wants from a row marked live (Overview: "The session preview has no
 * way to open the room").
 *
 * The actions are the Sessions list's and the calendar's, in the same words
 * and order (SessionEventPopover): the way in first — "Open the room", or
 * "Back to the room" once it is live — then Edit and Delete for whoever may
 * change it; Office Hours too, where Delete takes the whole desk after its own
 * dialog. A session that has finished offers its recording in the room's
 * place. The way in opens the room in the hub
 * (HubSessionRoom), never the policyholder portal.
 */
const props = defineProps({
  row: { type: Object, required: true },
  titleId: { type: String, required: true },
  // canManage: Edit and Delete (owner, hub staff, a broker on their own sessions).
  // canEdit: Edit only (a broker on an agency session they may edit).
  canManage: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'join', 'edit', 'cancel', 'recording', 'delete-desk']);

const isOfficeHours = computed(() => props.row.kind === 'office_hours');
const isLive = computed(() => Boolean(props.row.is_live));
const hasEnded = computed(() => Boolean(props.row.has_ended));
// Today's rows carry no day; the week's do.
const isToday = computed(() => !props.row.day_label);

const timeRange = computed(() => props.row.start_label);

// "Back to the room" only for somebody who has been in it (roomVisits).
const joinLabel = computed(() => roomLabel(props.row.id, isLive.value));
const joinVariant = computed(() => (isLive.value ? 'danger' : isToday.value ? 'success' : 'ghost'));
// Office Hours gets Edit and Delete beside Open the room, as a session does:
// whoever may edit it, which the page decides.
const editable = computed(() => props.canManage || props.canEdit);
const cancellable = computed(() => props.canManage && !isOfficeHours.value);
// Offered Edit on an agency session, a broker sees Delete it may not press.
const deleteRefused = computed(() => !isOfficeHours.value && props.canEdit && !props.canManage);
</script>

<template>
  <div class="event-popover" data-testid="event-popover">
    <header class="event-popover__head">
      <h2 :id="titleId" class="event-popover__title">{{ row.title }}</h2>
      <button type="button" class="event-popover__close" aria-label="Close" data-testid="event-popover-close" @click="emit('close')">
        <IconClose color="currentColor" />
      </button>
    </header>

    <!-- Office Hours reads as itself on the card, but each slot is stored under
         its own name — the room is derived from it — so it is shown here. -->
    <p v-if="isOfficeHours && row.stored_title !== row.title" class="event-popover__stored">
      {{ row.stored_title }}
    </p>

    <dl class="event-popover__facts">
      <div>
        <dt>When</dt>
        <dd>{{ row.day_label ? `${row.day_label} ${timeRange}` : timeRange }}</dd>
      </div>
      <div v-if="isOfficeHours">
        <dt>On duty</dt>
        <dd>{{ row.moderator?.name ?? 'Nobody on duty' }}</dd>
      </div>
      <template v-else>
        <div v-if="row.moderator">
          <dt>Hosting</dt>
          <dd>{{ row.moderator.name }}</dd>
        </div>
        <div v-if="row.speakers?.length">
          <dt>Speaking</dt>
          <dd>{{ row.speakers.map((s) => s.name).join(', ') }}</dd>
        </div>
      </template>
    </dl>

    <footer
      v-if="!hasEnded || (row.has_recording && !isOfficeHours) || editable"
      class="event-popover__foot"
      data-testid="event-popover-actions"
    >
      <!-- The way in carries the video mark; the way back does not (prototype `calPop`). -->
      <XlButton
        v-if="!hasEnded"
        :variant="joinVariant"
        size="sm"
        data-testid="event-popover-join"
        @click="emit('join', row)"
      ><IconVideo v-if="!isLive" color="currentColor" size="13" />{{ joinLabel }}</XlButton>
      <XlButton
        v-else-if="row.has_recording && !isOfficeHours"
        variant="ghost"
        size="sm"
        data-testid="event-popover-recording"
        @click="emit('recording', row)"
      >Watch the recording</XlButton>

      <XlButton
        v-if="editable"
        variant="ghost"
        size="sm"
        data-testid="event-popover-edit"
        @click="emit('edit', row)"
      >Edit</XlButton>
      <XlButton
        v-if="cancellable"
        variant="ghost-danger"
        size="sm"
        data-testid="event-popover-cancel"
        @click="emit('cancel', row)"
      >Delete</XlButton>
      <XlTooltip v-else-if="deleteRefused" :text="DELETE_REFUSED" control>
        <XlButton variant="ghost-danger" size="sm" unavailable data-testid="event-popover-cancel">Delete</XlButton>
      </XlTooltip>
      <XlButton
        v-else-if="isOfficeHours && editable"
        variant="ghost-danger"
        size="sm"
        data-testid="event-popover-delete-desk"
        @click="emit('delete-desk', row)"
      >Delete</XlButton>
    </footer>
  </div>
</template>

<style scoped>
.event-popover__head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.event-popover__title {
  flex: 1;
  min-width: 0;
  margin: 0 0 2px;
  font-size: var(--xl-font-size-md);
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
}

/* As the calendar popover's (SessionEventPopover `.popover__close`). */
.event-popover__close {
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  margin: -4px -6px 0 0;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: none;
  color: var(--xl-color-outline);
  cursor: pointer;
}

.event-popover__close:hover { background: var(--xl-color-background); }

.event-popover__stored {
  margin: 0 0 10px;
  font-size: var(--xl-font-size-sm);
  color: var(--xl-color-outline);
}

.event-popover__facts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0 0;
}

.event-popover__facts div {
  display: flex;
  gap: 10px;
  font-size: 13px;
}

.event-popover__facts dt {
  min-width: 68px;
  color: var(--xl-color-outline);
}

.event-popover__facts dd {
  margin: 0;
  color: var(--xl-color-on-surface);
}

/* As the calendar popover's foot, run to the panel's edges. */
.event-popover__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 14px -16px -16px;
  padding: 12px 16px;
  border-top: 1px solid var(--xl-color-border-subtle, var(--xl-color-border));
  border-radius: 0 0 var(--xl-radius-lg, 12px) var(--xl-radius-lg, 12px);
  background: var(--xl-color-background);
}
</style>
